import { useGlobalNotifier, useSpreadsheetState } from "@vrf/fr-vrf-core-react";
import { FrVrfSpreadsheet } from "@vrf/fr-vrf-spreadsheet-react";
import {
  FrVrfAccordion,
  FrVrfButton,
  FrVrfButtonPanel,
  FrVrfCheckboxField,
  FrVrfFormPanel,
  FrVrfInputDaterangeField,
  FrVrfPanel,
  FrVrfRadioButtonField,
  FrVrfSingleLineInputField,
} from "@vrf/fr-vrf-web-components-react";
import { stringify } from "../../util/querystring-util";
import React, { useEffect, useMemo } from "react";
import { createPath, useLocation, useNavigate } from "react-router-dom";
import { SpreadsheetState, usePageState } from ".";
import { useAppState } from "../../app";
import { useGetDeploymentRequests } from "../../services/deployment-requests";
import { removeMilliseconds } from "../../util/date-util";
import {
  DeploymentRequestType,
  EnvTypeCd,
  InDefCommonFlagType,
  TcktStsTypeIfPrdDplymnt,
  TcktStsTypeIfStgDplymnt,
} from "../../util/general-code/enums/general-codes";
import { usePageNavigation } from "../use-page-navigation";

const TEMPLATE_FILE_URL = "spreadsheet-template/deployment-request-list.xlsx";

export const DeploymentRequestListPageContent = () => {
  const [_appState, setAppState] = useAppState();
  const [pageState, setPageState] = usePageState();

  const { resetUserError, errorMessages } = useGlobalNotifier();
  const { isModalPage, initUserError } = usePageNavigation();

  const search = useLocation().search;
  const query = new URLSearchParams(search);
  const navigate = useNavigate();
  const params = useMemo(() => {
    return {
      pjtId: query.get("pjtId") ?? "",
      schedDateStart: query.get("schedDateStart") ?? "",
      schedDateEnd: query.get("schedDateEnd") ?? "",
      incClosed:
        query.get("incClosed") === InDefCommonFlagType.YES ? true : false,
      dipByRgn: query.get("dipByRgn") ?? "",
      dplymntEnv: query.get("dplymntEnv") ?? EnvTypeCd.STG,
    };
  }, [query]);

  const [getDeploymentRequestsResponse, sendGetDeploymentRequestsRequest] =
    useGetDeploymentRequests();

  const toDplymntStsList = (incClosed: boolean, dplymntEnv: string) => {
    let dplymntStsList: string[] = [];
    if (incClosed) {
      dplymntStsList = [];
    } else if (dplymntEnv === EnvTypeCd.STG) {
      dplymntStsList = [
        TcktStsTypeIfStgDplymnt.DEPLOYMENT_APPROVAL_PENDING,
        TcktStsTypeIfStgDplymnt.DEPLOYMENT_PENDING,
        TcktStsTypeIfStgDplymnt.DEPLOYING,
        TcktStsTypeIfStgDplymnt.WAITING_FOR_RETRY,
        TcktStsTypeIfStgDplymnt.DEPLOYMENT_FAILED,
        TcktStsTypeIfStgDplymnt.REVERT_DEPLOYMENT_APPROVAL_PENDING,
        TcktStsTypeIfStgDplymnt.REVERT_DEPLOYMENT_PENDING,
        TcktStsTypeIfStgDplymnt.REVERT_DEPLOYING,
        TcktStsTypeIfStgDplymnt.REVERT_WAITING_FOR_RETRY,
        TcktStsTypeIfStgDplymnt.REVERT_DEPLOYMENT_FAILED,
      ];
    } else if (dplymntEnv === EnvTypeCd.PRD) {
      dplymntStsList = [
        TcktStsTypeIfPrdDplymnt.DEPLOYMENT_APPROVAL_PENDING,
        TcktStsTypeIfPrdDplymnt.DEPLOYMENT_PENDING,
        TcktStsTypeIfPrdDplymnt.DEPLOYING,
        TcktStsTypeIfPrdDplymnt.WAITING_FOR_RETRY,
        TcktStsTypeIfPrdDplymnt.DEPLOYMENT_FAILED,
        TcktStsTypeIfPrdDplymnt.REVERT_DEPLOYMENT_APPROVAL_PENDING,
        TcktStsTypeIfPrdDplymnt.REVERT_DEPLOYMENT_PENDING,
        TcktStsTypeIfPrdDplymnt.REVERT_DEPLOYING,
        TcktStsTypeIfPrdDplymnt.REVERT_WAITING_FOR_RETRY,
        TcktStsTypeIfPrdDplymnt.REVERT_DEPLOYMENT_FAILED,
      ];
    }
    return dplymntStsList;
  };

  const onInitialDisplayed = () => {
    initUserError();
    setAppState((prevState) => ({
      ...prevState,
      title: `Interface Deployment Request List`,
    }));
    setPageState((prevState) => ({
      ...prevState,
      dipByRgn: params.dipByRgn,
      schedDateRange: {
        startDate: params.schedDateStart,
        endDate: params.schedDateEnd,
      },
      incClosed: params.incClosed,
      dplymntEnv: params.dplymntEnv,
    }));
    const incClosed = params.incClosed;
    const dplymntEnv = params.dplymntEnv;
    const dplymntStsList: string[] = toDplymntStsList(incClosed, dplymntEnv);
    sendGetDeploymentRequestsRequest({
      dplymntReqType: DeploymentRequestType.INTERFACE,
      pjtId: params.pjtId,
      dipByRgn: params.dipByRgn,
      schedDtimeStart: params.schedDateStart,
      schedDtimeEnd: params.schedDateEnd,
      dplymntStsList: dplymntStsList,
      dplymntEnv: params.dplymntEnv,
    });
  };

  const onGetDeploymentRequestListRequestResponded = () => {
    setPageState((prevState) => ({
      ...prevState,
      dipByRgn: !!prevState.dipByRgn ? prevState.dipByRgn : params.dipByRgn,
      schedDateRange: {
        startDate: !!prevState.schedDateRange.startDate
          ? prevState.schedDateRange.startDate
          : params.schedDateStart,
        endDate: !!prevState.schedDateRange.endDate
          ? prevState.schedDateRange.endDate
          : params.schedDateEnd,
      },
      incClosed: !!prevState.incClosed ? prevState.incClosed : params.incClosed,
      dplymntEnv: !!prevState.dplymntEnv
        ? prevState.dplymntEnv
        : params.dplymntEnv,
      searchResult: {
        ...prevState.searchResult,
        deploymentRequestList:
          getDeploymentRequestsResponse?.deploymentRequestList ?? [],
      },
    }));
  };

  useEffect(onInitialDisplayed, []);
  useEffect(onGetDeploymentRequestListRequestResponded, [
    getDeploymentRequestsResponse,
  ]);

  const onCellClicked = (event: CustomEvent) => {
    const eventName: string = event.detail.name;
    const dplymntReqSeq: string = event.detail.value;

    if (eventName === "transition") {
      const targetDeploymentRequest =
        pageState.searchResult.deploymentRequestList.find(
          (deploymentRequest) =>
            deploymentRequest.dplymntReqSeq === dplymntReqSeq,
        );

      const href = createPath({
        pathname: `/deployment-request-detail?dplymntReqSeq=${dplymntReqSeq}&screenMode=${targetDeploymentRequest?.screenMode}`,
      });
      navigate(href);
    }
    if (eventName === "reqNo_newTabIcon") {
      const targetDeploymentRequest =
        pageState.searchResult.deploymentRequestList.find(
          (deploymentRequest) =>
            deploymentRequest.dplymntReqSeq === dplymntReqSeq,
        );
      const href = createPath({
        pathname: `/deployment-request-detail?dplymntReqSeq=${dplymntReqSeq}&screenMode=${targetDeploymentRequest?.screenMode}`,
      });
      window.open(window.location.pathname + "#" + href);
    }
  };

  const [_spreadsheetState, _setSpreadsheetState, initSpreadsheetState] =
    useSpreadsheetState<SpreadsheetState>();
  const spreadsheetInitialData = useMemo<SpreadsheetState>(
    () => ({
      deploymentRequestList: pageState.searchResult.deploymentRequestList,
      removeMilliseconds,
    }),
    [pageState.searchResult],
  );

  /**
   * SearchContent
   */
  const multiItemError = (item: string[]) => {
    let errorMessage = "";
    item.forEach((e) => {
      if (errorMessages(e, isModalPage())) {
        errorMessage =
          errorMessage + e + ":" + errorMessages(e, isModalPage()) + ",";
      }
    });
    return errorMessage.substring(0, errorMessage.length - 1);
  };

  const onChangeValueHandle = (name: string) => (event: CustomEvent) => {
    if (["dipByRgn", "incClosed", "dplymntEnv"].includes(name)) {
      setPageState((prevState) => ({
        ...prevState,
        [name]: event.detail.value,
      }));
    }
    if (["schedDateRange"].includes(name)) {
      const schedDateRange = {
        startDate: event.detail.startDate,
        endDate: event.detail.endDate,
      };
      setPageState((prevState) => ({
        ...prevState,
        [name]: schedDateRange,
      }));
    }
  };

  const onClickSearch = () => {
    resetUserError();
    const newParams = {
      pjtId: params.pjtId,
      dipByRgn: pageState.dipByRgn,
      schedDateStart: pageState.schedDateRange.startDate,
      schedDateEnd: pageState.schedDateRange.endDate,
      incClosed: pageState.incClosed
        ? InDefCommonFlagType.YES
        : InDefCommonFlagType.NO,
      dplymntEnv: pageState.dplymntEnv,
    };
    const href = createPath({
      pathname: `/interface-deployment-request-list?${stringify(newParams)}`,
    });
    navigate(href);

    const incClosed = pageState.incClosed;
    const dplymntEnv = pageState.dplymntEnv;
    const dplymntStsList: string[] = toDplymntStsList(incClosed, dplymntEnv);
    sendGetDeploymentRequestsRequest({
      dplymntReqType: DeploymentRequestType.INTERFACE,
      pjtId: params.pjtId,
      dipByRgn: pageState.dipByRgn,
      schedDtimeStart: pageState.schedDateRange.startDate,
      schedDtimeEnd: pageState.schedDateRange.endDate,
      dplymntStsList: dplymntStsList,
      dplymntEnv: pageState.dplymntEnv,
    });
  };

  return (
    <>
      {/* Search Content */}
      <FrVrfAccordion>
        <FrVrfPanel>
          <FrVrfFormPanel columns={3}>
            <FrVrfSingleLineInputField
              labelContent="DIP By Region"
              content={pageState.dipByRgn}
              onInputValueChanged={onChangeValueHandle("dipByRgn")}
              errorMessage={errorMessages(
                pageState.fieldPath.dipByRgn,
                isModalPage(),
              )}
            />
            <FrVrfInputDaterangeField
              labelContent="Scheduled Deployment Date"
              inputDateRange={pageState.schedDateRange}
              dateFormat="ymd"
              clearButtonVisible
              onInputValueChanged={onChangeValueHandle("schedDateRange")}
              errorMessage={multiItemError([
                pageState.fieldPath.schedDateStart,
                pageState.fieldPath.schedDateEnd,
              ])}
            />
            <div></div>
            <FrVrfCheckboxField
              labelContent="Include closed"
              checked={pageState.incClosed}
              onCheckedChanged={onChangeValueHandle("incClosed")}
              errorMessage={errorMessages(
                pageState.fieldPath.incClosed,
                isModalPage(),
              )}
            />
            <FrVrfRadioButtonField
              labelContent="STG/PRD"
              optionItems={[
                { label: "STG", value: EnvTypeCd.STG },
                { label: "PRD", value: EnvTypeCd.PRD },
              ]}
              checked={pageState.dplymntEnv}
              onRadioCheckedChanged={onChangeValueHandle("dplymntEnv")}
              errorMessage={errorMessages(
                pageState.fieldPath.dplymntEnv,
                isModalPage(),
              )}
            />
          </FrVrfFormPanel>
          <FrVrfButtonPanel>
            <FrVrfButton
              content="Search"
              theme="primary"
              size="large"
              onClicked={onClickSearch}
              authActId="deployment_request_lost.button.search"
            />
          </FrVrfButtonPanel>
        </FrVrfPanel>
      </FrVrfAccordion>
      <FrVrfPanel>
        <FrVrfSpreadsheet
          data={spreadsheetInitialData}
          onInitialized={initSpreadsheetState}
          templateUrl={TEMPLATE_FILE_URL}
          onCellClicked={onCellClicked}
          maxHeight={800}
        />
      </FrVrfPanel>
    </>
  );
};
